import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Paper, Title, Text, Group, Stack, Badge, Loader, Center, Alert, ActionIcon, Tooltip } from '@mantine/core'
import { IconAlertCircle, IconShieldCheck, IconX } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { getLoginFlags, dismissLoginFlag, getErrorMessage } from '../../api/loginActivityApi'
import type { FlaggedUser, LoginAnomalyFlagName } from '../../api/loginActivityApi'
import { FLAG_LABELS, SEVERITY_COLORS } from '../../utils/loginAnomaly'

const TOP_N = 10

function formatRelative(value: string): string {
  const diffMs = Date.now() - new Date(value).getTime()
  const hours = Math.round(diffMs / (60 * 60 * 1000))
  if (hours < 1) return 'just now'
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

export function FlaggedAccountsCard() {
  const navigate = useNavigate()
  const [users, setUsers] = useState<FlaggedUser[]>([])
  const [windowDays, setWindowDays] = useState<number | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    getLoginFlags()
      .then((res) => {
        setUsers(res.users)
        setWindowDays(res.windowDays)
        setLoading(false)
      })
      .catch((err) => {
        setError(getErrorMessage(err))
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleDismiss = useCallback(
    async (e: React.MouseEvent, userId: string, flag: LoginAnomalyFlagName) => {
      e.stopPropagation()
      try {
        await dismissLoginFlag(userId, { flag })
        notifications.show({
          message: `${FLAG_LABELS[flag]} snoozed for 30 days`,
          color: 'green',
        })
        load()
      } catch (err) {
        notifications.show({
          title: 'Dismiss failed',
          message: getErrorMessage(err),
          color: 'red',
        })
      }
    },
    [load]
  )

  return (
    <Paper withBorder radius="md" p="lg">
      <Group justify="space-between" mb="md">
        <Title order={4}>Flagged Accounts</Title>
        {!loading && !error && (
          <Text size="sm" c="dimmed">
            {users.length} in the last {windowDays ?? 7} days
          </Text>
        )}
      </Group>

      {loading && (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {error}
        </Alert>
      )}

      {!loading && !error && users.length === 0 && (
        <Group gap="xs" c="dimmed" py="sm">
          <IconShieldCheck size={18} />
          <Text size="sm">No flagged accounts</Text>
        </Group>
      )}

      {!loading && !error && users.length > 0 && (
        <Stack gap="xs">
          {users.slice(0, TOP_N).map((u) => (
            <Paper
              key={u.userId}
              withBorder
              p="sm"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/users?userId=${u.userId}`)}
            >
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <div>
                  <Text fw={500} size="sm">{u.email ?? u.userId}</Text>
                  <Group gap={4} mt={4}>
                    {u.flags.map((f) => (
                      <Badge
                        key={f.flag}
                        color={SEVERITY_COLORS[f.severity]}
                        variant="light"
                        size="sm"
                        rightSection={
                          <Tooltip label="Snooze for 30 days">
                            <ActionIcon
                              size="xs"
                              variant="transparent"
                              color={SEVERITY_COLORS[f.severity]}
                              onClick={(e) => handleDismiss(e, u.userId, f.flag)}
                            >
                              <IconX size={10} />
                            </ActionIcon>
                          </Tooltip>
                        }
                      >
                        {FLAG_LABELS[f.flag]}
                      </Badge>
                    ))}
                  </Group>
                </div>
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {formatRelative(u.mostRecentEventAt)}
                </Text>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  )
}
